import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import ButtonBase from '@mui/material/ButtonBase';
import Chip from '@mui/material/Chip';
import ArrowForwardOutlined from '@mui/icons-material/ArrowForwardOutlined';
import LinkOutlined from '@mui/icons-material/LinkOutlined';
import PersonSearchOutlined from '@mui/icons-material/PersonSearchOutlined';
import PlayCircleOutlineOutlined from '@mui/icons-material/PlayCircleOutlineOutlined';
import AssignmentTurnedInOutlined from '@mui/icons-material/AssignmentTurnedInOutlined';
import ROUTES from '../../core/constants/routes.constants';
import PortalLayout from '../../components/layouts/PortalLayout';
import { theme } from '@omnigenbiodata/ui';
import { useTranslation } from 'react-i18next';

const primaryTint = theme.palette.grey[100];
const successTint = theme.palette.success.lighter;

interface HomeActionCardProps {
  title: string;
  description: string;
  to?: string;
  icon: React.ReactNode;
  cta: string;
  primary?: boolean;
  disabled?: boolean;
}

function HomeActionCard({ title, description, to, icon, cta, primary = false, disabled = false }: HomeActionCardProps) {
  const cardContent = (
    <Paper
      elevation={4}
      sx={{
        height: '100%',
        border: 1,
        borderColor: disabled ? 'divider' : primary ? theme.palette.grey[300] : 'rgba(16, 53, 68, 0.18)',
        bgcolor: disabled ? 'grey.50' : 'background.paper',
        overflow: 'hidden',
        opacity: disabled ? 0.65 : 1,
        transition: 'all 0.2s ease-in-out',
        '&:hover': disabled
          ? undefined
          : {
              transform: 'translateY(-2px)',
              boxShadow: 6,
              borderColor: theme.palette.grey[500],
            },
      }}
    >
      <Box
        sx={{
          height: primary ? 12 : 8,
          bgcolor: primary ? 'success.main' : 'primary.main',
        }}
      />

      <Box p={{ xs: 3, md: primary ? 5 : 4 }}>
        <Box
          sx={{
            width: primary ? 100 : 76,
            height: primary ? 100 : 76,
            borderRadius: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: disabled ? 'grey.100' : primary ? successTint : primaryTint,
            mb: 3,
          }}
        >
          {icon}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
          <Typography
            variant={primary ? 'h4' : 'h5'}
            component="h2"
            sx={{
              fontWeight: 700,
              lineHeight: 1.1,
              color: 'primary.main',
            }}
          >
            {title}
          </Typography>

          {disabled && (
            <Chip label="Coming soon" color="default" size="small" variant="outlined" sx={{ fontWeight: 700 }} />
          )}
        </Box>

        <Typography
          variant={primary ? 'h6' : 'body1'}
          component="p"
          color="text.secondary"
          sx={{
            mb: 3,
            lineHeight: 1.35,
            maxWidth: primary ? 620 : 460,
          }}
        >
          {description}
        </Typography>

        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1,
            color: disabled ? 'text.disabled' : 'primary.main',
          }}
        >
          <Typography variant="h6" component="span" sx={{ fontWeight: 700 }}>
            {disabled ? 'Not available yet' : cta}
          </Typography>

          {!disabled && <ArrowForwardOutlined fontSize="large" />}
        </Box>
      </Box>
    </Paper>
  );

  if (disabled || !to) {
    return cardContent;
  }

  return (
    <ButtonBase
      component={Link}
      to={to}
      sx={{
        display: 'block',
        width: '100%',
        height: '100%',
        textAlign: 'left',
        borderRadius: 3,
      }}
    >
      {cardContent}
    </ButtonBase>
  );
}

function HomeScene() {
  const { t } = useTranslation('portal');
  return (
    <PortalLayout>
      <Box pb={3}>
        <Paper
          elevation={4}
          sx={{
            mb: 4,
            border: 1,
            borderColor: 'rgba(16, 53, 68, 0.18)',
            bgcolor: primaryTint,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              height: 12,
              bgcolor: 'primary.main',
            }}
          />

          <Box p={{ xs: 3, md: 5 }}>
            <Typography
              variant="h3"
              component="h1"
              sx={{
                fontWeight: 700,
                mb: 1,
                color: 'primary.main',
              }}
            >
              {t('home.title')}
            </Typography>

            <Typography
              variant="h6"
              component="p"
              color="text.secondary"
              sx={{
                maxWidth: 900,
                lineHeight: 1.35,
              }}
            >
              {t('home.text')}
            </Typography>
          </Box>
        </Paper>

        <Grid container spacing={3}>
          <Grid item xs={12} md={7}>
            <HomeActionCard
              primary
              title={t('home.links.samples.title')}
              description={t('home.links.samples.text')}
              to={ROUTES.portal}
              cta={t('home.links.samples.link')}
              icon={<LinkOutlined color="success" sx={{ fontSize: 62 }} />}
            />
          </Grid>

          <Grid item xs={12} md={5}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <HomeActionCard
                  title={t('home.links.pis.title')}
                  description={t('home.links.pis.text')}
                  to={ROUTES.info}
                  cta={t('home.links.pis.link')}
                  icon={<PersonSearchOutlined color="primary" sx={{ fontSize: 46 }} />}
                />
              </Grid>

              <Grid item xs={12}>
                <HomeActionCard
                  title={t('home.links.video.title')}
                  description={t('home.links.video.text')}
                  to={ROUTES.video}
                  cta={t('home.links.video.link')}
                  icon={<PlayCircleOutlineOutlined color="primary" sx={{ fontSize: 46 }} />}
                />
              </Grid>

              <Grid item xs={12}>
                <HomeActionCard
                  title={t('home.links.enrolment.title')}
                  description={t('home.links.enrolment.text')}
                  cta={t('home.links.enrolment.link')}
                  icon={<AssignmentTurnedInOutlined color="disabled" sx={{ fontSize: 46 }} />}
                  disabled
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </PortalLayout>
  );
}

export default HomeScene;
